import React, { useState, useEffect, useRef } from 'react';
import { Table } from 'rsuite';
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  MouseSensor,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import { SortableItem } from '../../atoms/sortableItem/sortableItem';
import './social-result-container.scss';
import { default as t } from '../../../utils/i18n';

const { Column, HeaderCell, Cell } = Table;

function SocialResultContainer(props) {
  const [panels, setPanels] = useState([]);
  const TableData = useRef([]);

  useEffect(() => {
    let lastValue = props?.data[0].groups[Object.keys(props?.data[0]?.groups).pop()];
    let temp = convertObjectHighestLevel(lastValue[0].data);
    TableData.current = temp;
    let commentary = [];
    if (props?.data[0]?.commentary_to_show) {
      commentary = props?.data[0]?.commentary_to_show?.map((list, index) => ({
        id: index,
        content: list.value,
        editable: false,
        show_in_export: list.show_in_export,
      }));
    }
    setPanels([...commentary]);
  }, []);

  function convertObjectHighestLevel(input) {
    const outputObject = {};

    const combineObjects = obj => {
      const { breakdownId, ...rest } = obj;
      const [key1, key2, key3] = breakdownId;

      if (key1 && key2 && key3) {
        if (!outputObject[key1]) {
          outputObject[key1] = { [key2]: { [key3]: rest } };
        } else if (!outputObject[key1][key2]) {
          outputObject[key1][key2] = { [key3]: rest };
        } else {
          outputObject[key1][key2][key3] = rest;
        }
      } else if (key1 && key2) {
        if (!outputObject[key1]) {
          outputObject[key1] = { [key2]: rest };
        } else {
          outputObject[key1][key2] = { ...outputObject[key1][key2], ...rest };
        }
      } else if (key1) {
        outputObject[key1] = { ...outputObject[key1], ...rest };
      }
    };

    if (Array.isArray(input)) {
      for (const obj of input) {
        combineObjects(obj);
      }
    } else {
      combineObjects(input);
    }

    return generateNestedArray(outputObject);
  }

  const generateNestedArray = obj => {
    const arrayData = Object.entries(obj).map(([key1, value1]) => {
      const nestedObjects = Object.entries(value1).map(([key2, value2]) => ({
        [key2]: value2,
      }));
      return {
        [key1]: nestedObjects.reduce((acc, cur) => ({ ...acc, ...cur }), {}),
      };
    });

    return arrayData;
  };

  const changeMetricsName = key => {
    const kpiList = props?.kpiList?.length > 0 ? props?.kpiList : [];
    const metric = kpiList?.filter(x => x?.sys_id === key)[0];
    let data_type = metric?.data_type;
    let type = '';
    if(data_type === 'PERCENTAGE'){
      type = '(%) ';
    } else if (data_type === 'CURRENCY') {
      type = `(${props?.currency}) `;
    }
    return type + metric?.KPI_Metric;
  };

  const MultiplePanels = () => {
    const mouseSensor = useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10, // Enable sort function when dragging 10px   💡 here!!!
      },
    });
    const sensors = useSensors(mouseSensor);
    function handleDragEnd(event) {
      const { active, over } = event;

      if (active.id !== over.id) {
        const oldIndex = panels.findIndex(x => x.id === active.id);
        const newIndex = panels.findIndex(x => x.id === over.id);
        const newArray = arrayMove(panels, oldIndex, newIndex);
        updatePanelsData(newArray);
      }
    }
    const updatePanelsData = (comentryData, type = '') => {
      setPanels(comentryData);
      if (type !== 'edit') {
        props?.updateReportPreference('breakdown', comentryData, props?.data[0]?.id);
      }
    };

    return (
      <>
        {props?.isEdit ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            autoScroll={false}
          >
            <SortableContext items={panels} strategy={verticalListSortingStrategy}>
              {panels.map(item => (
                <SortableItem
                  data={item}
                  panels={panels}
                  fromSnapShot={props?.fromSnapShot}
                  isEdit={props?.isEdit}
                  setPanels={(data, type) => updatePanelsData(data, type)}
                />
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          <div>
            {panels.map(item =>
              !item?.show_in_export && props?.fromSnapShot ? (
                ''
              ) : (
                <SortableItem
                  data={item}
                  isEdit={props?.isEdit}
                  panels={panels}
                  setPanels={(data, type) => updatePanelsData(data, type)}
                />
              )
            )}
          </div>
        )}
      </>
    );
  };

  const createTable = outputArray => {
    const columns = [];
    const data = [];

    if (outputArray.length > 0) {
      const firstObj = outputArray[0];

      // Create columns from the object keys
      Object.keys(firstObj).forEach(key => {
        if (typeof firstObj[key] === 'object') {
          const firstChild = Object.keys(firstObj[key]);
          const secondChild = firstObj[key][firstChild[0]] && Object.keys(firstObj[key][firstChild[0]]);
          if (typeof firstObj[key][firstChild[0]] === 'number' || firstObj[key][firstChild[0]] === null) {
            Object.keys(firstObj[key]).forEach(nestedKey => {
              // for level1
              columns.push({ name: nestedKey });
            });
          } else if (typeof firstObj[key][firstChild[0]][secondChild[0]]  === 'number'|| firstObj[key][firstChild[0]][secondChild[0]] === null) {
            firstObj[key][firstChild[0]] && Object.keys(firstObj[key][firstChild[0]]).forEach(nestedKey => {
              // for level2
              columns.push({ name: nestedKey });
            });
          } else {
            firstObj[key][firstChild[0]][secondChild[0]] && Object.keys(firstObj[key][firstChild[0]][secondChild[0]])?.forEach(nestedKey => {
              // for level2 with 3attributes
              columns.push({ name: nestedKey });
            });
          }
        }
      });

      // Create data rows
      outputArray.forEach(obj => {
        const parentKey = Object.keys(obj)[0];
        const firstChild = Object.keys(obj[parentKey]);
        const secondChild = obj[parentKey][firstChild[0]] && Object.keys(obj[parentKey][firstChild[0]]) ;
        if (typeof obj[parentKey][firstChild[0]] === 'number' || obj[parentKey][firstChild[0]] === null) {
          const row = {
            key: Object.keys(obj)[0],
          };

          Object.entries(obj).forEach(([key, value]) => {
            if (value && typeof value === 'object') {
              Object.entries(value).forEach(([nestedKey, nestedValue]) => {
                row[`${nestedKey}`] = nestedValue ? !Number.isInteger(nestedValue) ? nestedValue?.toFixed(2) : nestedValue: 'N/A';
              });
            }
          });

          data.push(row);
        } else if (typeof obj[parentKey][firstChild[0]][secondChild[0]] === 'number' || obj[parentKey][firstChild[0]][secondChild[0]] === null) {
          Object.entries(obj).forEach(([parentKey, parentValue]) => {
            const firstChild = Object.keys(parentValue);
            data.push({ key: parentKey, flag:'parent' });
            firstChild.forEach(ele => {
              const row = { key: ele };

              parentValue[ele] &&  Object.entries(parentValue[ele]).forEach(([nestedKey, nestedValue]) => {
                if (nestedValue && typeof nestedValue === 'object') {
                  Object.entries(nestedValue).forEach(([subNestedKey, subNestedValue]) => {
                    row[`${nestedKey}`] = subNestedValue;
                  });
                } else {
                  row[nestedKey] = nestedValue
                    ? !Number.isInteger(nestedValue)
                      ? nestedValue?.toFixed(2)
                      : nestedValue
                    : 'N/A';
                }
              });
              data.push(row);
            });
          });
        } else {
          Object.entries(obj).forEach(([parentKey, parentValue]) => {
            const firstChild = Object.keys(parentValue);
            data.push({ key: parentKey, flag:'parent' });
            Object.entries(parentValue).forEach(([childKey, childValue]) => {
              const secondChild = childValue && Object.keys(childValue);
              data.push({ key: childKey , flag:'child'});

              secondChild && secondChild.forEach(ele => {
                const row = { key: ele };

                childValue[ele] && Object.entries(childValue[ele])?.forEach(([nestedKey, nestedValue]) => {
                  if (nestedValue && typeof nestedValue === 'object') {
                    Object.entries(nestedValue).forEach(([subNestedKey, subNestedValue]) => {
                      row[`${nestedKey}`] = subNestedValue;
                    });
                  } else {
                    row[nestedKey] = nestedValue
                      ? !Number.isInteger(nestedValue)
                        ? nestedValue?.toFixed(2)
                        : nestedValue
                      : 'N/A';
                  }
                });
                data.push(row);
              });
            });
          });
        }
      });
    }

    return { columns, data };
  };

  function splitter(arr, length = 8) {
    const result = arr.reduce((resultArray, item, index) => {
      const chunkIndex = Math.floor(index / length);
      if (!resultArray[chunkIndex]) {
        resultArray[chunkIndex] = []; // start a new chunk
      }
      resultArray[chunkIndex].push(item);
      return resultArray;
    }, []);
    return result;
  }

  function getTheKeys(key) {
    let result = '';
    if (!isJson(key)) {
      result = key;
    } else {
      if (Array.isArray(JSON?.parse(key))) {
        let val = JSON?.parse(key);
        result = val?.length > 0 ? val.join(',') : val?.length <= 0 ? 'N/A' : val;
      } else {
        let data = JSON?.parse(key);
        let objKeys = Object.keys(data);
        if (objKeys?.length > 0) {
          objKeys.forEach(k => {
            data[k] = data[k]?.length ? data[k] : 'N/A';
            result = result !== '' ? `${result}, ${k}: ${data[k]}` : `${k}: ${data[k]}`;
          });
        } else {
          result = 'N/A';
        }
      }
    }
    return result;
  }

  function isJson(str) {
    try {
        JSON.parse(str);
    } catch (e) {
        return false;
    }
    return true;
}

  const RsuiteTable = ({ outputArray }) => {
    const { columns, data } = createTable(outputArray);
    let tableSplit = [];
    if (columns?.length > 8 && props?.fromSnapShot) {
      tableSplit = splitter(columns);
    } else {
      tableSplit = [[...columns]];
    }
    const convertValue = (value) => {
      return value && value !== 'N/A'? Number(value)?.toLocaleString() : value;
    }
    return (
      <div>
        {tableSplit.map(item => (
          <div className="break-table">
            <Table
              autoHeight
              bordered
              cellBordered
              data={data}
              wordWrap={props?.fromSnapShot}
              headerHeight={props?.fromSnapShot ? 80 : 40}
              width={item.length <= 7 ? 300 + item.length * (window.innerWidth / 11) : null}
            >
              <Column width={300} fixed ={item.length > 7}>
                <HeaderCell style={{ backgroundColor: '#F5F5F5' }} title={props?.data[0].title}>
                  {props?.data[0].title}
                </HeaderCell>
                <Cell style={{ backgroundColor: '#F6F4EE' }}>
                  {rowData => {
                    return <span className={rowData?.flag === 'parent' ? 'parent-key' : rowData?.flag === 'child' ? 'child-key' : 'rowData'} 
                    title={getTheKeys(rowData?.key)}>{getTheKeys(rowData?.key)}</span>;
                  }}
                </Cell>
              </Column>
              {item.map(({ name, key }) => (
                <Column key={key}  width= {window.innerWidth / 11}>
                  <HeaderCell
                    style={{ backgroundColor: '#F5F5F5' }}
                    title={changeMetricsName(name)}
                    headerHeight={60}
                    align='left'
                  >
                    {changeMetricsName(name)}
                  </HeaderCell>
                  <Cell align='right'>
                    {rowData => {
                      return <span className={rowData[name] === 'N/A' ? 'notAvailableText':''} title={rowData[name]}>{convertValue(rowData[name])}</span>;
                    }}
                  </Cell>
                </Column>
              ))}
            </Table>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="final-report-main-container">
      <div className="table-Box social">
        {TableData.current.length > 0 && <RsuiteTable outputArray={TableData.current} />}
      </div>
      {panels?.length > 0 ? (
        <div className="insights">
          <h3>{t('final.output.insights.title')}</h3>
          <MultiplePanels />
          <div className='the-insights'><span className='high-light'>*</span>{t('report.output.insights.note')} 
          </div>
        </div>
      ) : (
        <div className="insights">
        <h3>{t('final.output.insights.title')}</h3>
        <p className='no-insights'>{t('final.output.no.insights')}</p>
        </div>
      )}
    </div>
  );
}

export default SocialResultContainer;
